// Cookie-backed storage adapter for Supabase auth, used to share the session
// across subdomains of the configured root domain. Falls back to localStorage
// when the page is served from a hostname that isn't a subdomain of the root
// (e.g. plain `localhost` during E2E or unit tests).

interface CookieOptions {
  domain?: string;
  secure: boolean;
  sameSite: "lax";
  maxAge?: number;
  path: string;
}

const REMEMBER_KEY = "min-konto.remember";
const SESSION_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${encodeURIComponent(name)}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string, opts: CookieOptions): void {
  if (typeof document === "undefined") return;
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=${opts.path}`;
  if (opts.domain) cookie += `; Domain=${opts.domain}`;
  if (opts.maxAge !== undefined) cookie += `; Max-Age=${opts.maxAge}`;
  cookie += `; SameSite=${opts.sameSite}`;
  if (opts.secure) cookie += "; Secure";
  // biome-ignore lint/suspicious/noDocumentCookie: Supabase storage adapter is sync; Cookie Store API is async.
  document.cookie = cookie;
}

function deleteCookie(name: string, domain: string | undefined): void {
  writeCookie(name, "", {
    domain,
    secure: false,
    sameSite: "lax",
    maxAge: 0,
    path: "/",
  });
}

interface StorageBackend {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function localStorageBackend(): StorageBackend | null {
  if (typeof window === "undefined") return null;
  try {
    const probe = "__probe__";
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

function deriveCookieDomain(rootDomain: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname;
  if (host === rootDomain) return `.${rootDomain}`;
  if (host.endsWith(`.${rootDomain}`)) return `.${rootDomain}`;
  return undefined;
}

function shouldUseCookies(rootDomain: string): boolean {
  return deriveCookieDomain(rootDomain) !== undefined;
}

interface MakeStorageOptions {
  rootDomain: string;
}

export function makeStorage({
  rootDomain,
}: MakeStorageOptions): StorageBackend {
  const cookieDomain = deriveCookieDomain(rootDomain);
  const local = localStorageBackend();
  const cookies = shouldUseCookies(rootDomain);

  if (!cookies) {
    if (!local) {
      const noop = (): null => null;
      return {
        getItem: noop,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
    }
    return local;
  }

  const useSecure =
    typeof window !== "undefined" && window.location.protocol === "https:";

  return {
    getItem(key: string): string | null {
      const fromCookie = readCookie(key);
      if (fromCookie !== null) return fromCookie;
      return local?.getItem(key) ?? null;
    },
    setItem(key: string, value: string): void {
      const remember = (local?.getItem(REMEMBER_KEY) ?? "1") !== "0";
      writeCookie(key, value, {
        domain: cookieDomain,
        secure: useSecure,
        sameSite: "lax",
        maxAge: remember ? SESSION_COOKIE_MAX_AGE : undefined,
        path: "/",
      });
    },
    removeItem(key: string): void {
      deleteCookie(key, cookieDomain);
      local?.removeItem(key);
    },
  };
}
